import { Button } from "@material-tailwind/react";
import { useQuery } from "@tanstack/react-query";
import bannerImg from "assets/images/Building-Good-in-2020-scaled.jpg";
import Section from "components/Section";
import AspectRatioDiv from "components/utils/AspectRatioDiv";
import Spinner from "components/utils/Spinner";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactPaginate from "react-paginate";
import { Link } from "react-router-dom";
import TextTruncate from "react-text-truncate";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import axios from "utils/axios";
import getMonth from "utils/getMonth";
import resolveImgUrl from "utils/resolveImgUrl";
import { Event } from "utils/types";

const Events = () => {
  const [, setSignal] = useState({});
  const forceRerender = () => {
    setSignal({});
  };

  const {
    data: banners,
    status: bannersStatus,
    error: bannersError,
    refetch: refetchBanners,
  } = useQuery(["events", "banners"], async () => {
    const { data } = await axios.get(`events/banners`);
    return data as Event[];
  });

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const {
    data: latest,
    status: latestStatus,
    error: latestError,
    refetch: refetchLatest,
  } = useQuery(["events", { limit: 10, upcoming: true }], async () => {
    const { data } = await axios.get(
      `events?limit=10&page=${page}&upcoming=true`
    );
    setPageCount(Math.ceil(data[1] / 10));
    return data[0] as Event[];
  });

  const handlePageClick = (event: { selected: number }) => {
    setPage(event.selected + 1);
  };

  return (
    <div className="pb-20">
      <AspectRatioDiv className="pt-[100%] md:pt-[60%] lg:pt-[50%] xl:pt-[40%] 2xl:pt-[40%] group">
        {bannersStatus === "loading" ? (
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
            <Spinner twWidthHeight="w-20 h-20" />
          </div>
        ) : bannersStatus === "error" ? (
          <div className="h-24 grid place-content-center my-4">
            <div className="flex flex-col items-center gap-y-2">
              <i className="fas fa-circle-exclamation text-3xl"></i>
              <div className="text-xl font-semibold">
                {/* @ts-ignore */}
                {bannersError.response?.status || "Something Went Wrong"}
              </div>
              <Button onClick={() => refetchBanners()}>Reload</Button>
            </div>
          </div>
        ) : (
          <Swiper
            onSwiper={(swiper) => {
              swiper.on("slideChange", forceRerender);
            }}
            className="h-full w-full relative"
          >
            {banners.map((banner) => {
              const date = new Date(banner.eventDate);
              return (
                <SwiperSlide key={banner.id} className="h-full w-full relative">
                  <Link to={`${banner.slug}`}>
                    {/* <div className='absolute  top-0 left-0 w-1/6 h-full bg-gradient-to-r md:from-[#000000] to-[#00000000]'></div>
                <div className='absolute  top-0 right-0 w-1/6 h-full bg-gradient-to-l md:from-[#000000] to-[#00000000]'></div> */}
                    <div className="absolute top-0 left-0 w-full h-full bg-black opacity-30 transition-opacity group-hover:opacity-50"></div>

                    <img
                      src={resolveImgUrl(banner.imgUrl)}
                      className="w-full absolute top-0 left-0 object-cover z-[-2] object-center h-full"
                      alt=""
                    />

                    <div className="absolute z-[1] bottom-14 md:bottom-auto md:top-20 w-full pointer-events-none">
                      <Section
                        className="relative pt-0 flex flex-row-reverse"
                        isContainer
                      >
                        <div className=" flex text-white flex-col items-center w-[5.2rem] border border-white py-2">
                          <span className="text-xl md:text-2xl font-bold">
                            {getMonth(date.getMonth())
                              .toUpperCase()
                              .slice(0, 3)}
                          </span>
                          <span className="text-4xl md:text-5xl font-bold">
                            {date.getDate()}
                          </span>
                        </div>
                      </Section>
                    </div>

                    <div className="absolute top-0 h-full w-full">
                      <Section
                        isContainer
                        className="relative flex flex-col justify-between h-full pt-20 md:pb-6 2xl:pb-14 text-white"
                      >
                        <div className="w-2/3 lg:w-1/2 2xl:w-1/3 flex-col gap-y-6 flex">
                          <span
                            className="px-2 py-1 rounded-full text-sm w-max"
                            style={{
                              backgroundColor: `#${banner.category.color}`,
                            }}
                          >
                            {banner.category.title}
                          </span>
                          <h2 className="font-semibold text-2xl lg:text-3xl ">
                            <TextTruncate
                              text={banner.title}
                              line={3}
                              truncateText="..."
                            />
                          </h2>
                          <TextTruncate
                            text={banner.rawDescription}
                            line={4}
                            truncateText="..."
                          />
                        </div>
                        <div className="flex items-center gap-2 relative">
                          <div className="w-6 h-6 rounded-full">
                            {!banner.submittedBy.profilePicture ? (
                              <div className="w-full h-full rounded-full bg-white"></div>
                            ) : (
                              <img
                                src={resolveImgUrl(
                                  banner.submittedBy.profilePicture
                                )}
                                className="w-full h-full rounded-full object-cover"
                                alt=""
                              />
                            )}
                          </div>
                          <span className="text-xs">
                            {banner.submittedBy.name}
                          </span>
                        </div>
                      </Section>
                    </div>
                  </Link>
                </SwiperSlide>
              );
            })}
            <div className="absolute bottom-0 w-full">
              <Section
                isContainer
                className="relative md:pb-6 2xl:pb-14 flex flex-row-reverse"
              >
                <div className=" flex gap-2 text-gray-700 z-[1]">
                  <SwiperPrevButton />
                  <SwiperNextButton />
                </div>
              </Section>
            </div>
          </Swiper>
        )}
      </AspectRatioDiv>
      <Section isContainer className="relative min-h-[18rem]">
        <h2 className="text-2xl lg:text-3xl xl:text-4xl font-semibold my-5">
          Acara Mendatang
        </h2>
        {latestStatus === "loading" ? (
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
            <Spinner twWidthHeight="w-20 h-20" />
          </div>
        ) : latestStatus === "error" ? (
          <div className="h-24 grid place-content-center my-4">
            <div className="flex flex-col items-center gap-y-2">
              <i className="fas fa-circle-exclamation text-3xl"></i>
              <div className="text-xl font-semibold">
                {/* @ts-ignore */}
                {latestError.response?.status || "Something Went Wrong"}
              </div>
              <Button onClick={() => refetchLatest()}>Reload</Button>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
            {latest.map((event) => {
              const date = new Date(event.eventDate);
              return (
                <Link
                  to={event.slug}
                  className="relative flex flex-col shadow-around group overflow-hidden"
                >
                  <div className="relative h-40">
                    <div className="absolute z-[1] top-0 right-5 pointer-events-none bg-[#00000055]">
                      <div className=" flex text-white flex-col items-center w-[5.2rem] mt-8 md:mt-4 pb-4">
                        <span className="text-xl md:text-2xl font-bold">
                          {getMonth(date.getMonth()).toUpperCase().slice(0, 3)}
                        </span>
                        <span className="text-4xl md:text-5xl font-bold">
                          {date.getDate()}
                        </span>
                      </div>
                    </div>
                    <span
                      className="absolute left-5 top-5 px-2 py-1 rounded-full text-sm w-max text-white z-[2]"
                      style={{ backgroundColor: `#${event.category.color}` }}
                    >
                      {event.category.title}
                    </span>
                    <div className="absolute w-full h-full bg-black opacity-0 group-hover:opacity-20 transition z-[1]"></div>
                    <img
                      src={resolveImgUrl(event.imgUrl)}
                      alt=""
                      className="object-cover h-40 w-full transform group-hover:scale-110 transition-transform"
                    />
                  </div>
                  <div className="bg-white flex flex-col justify-between gap-y-4 p-5 pt-2 h-full z-[2]">
                    <div className=" flex-col gap-y-2 flex">
                      <h2 className="font-semibold text-2xl">
                        <TextTruncate
                          text={event.title}
                          line={2}
                          truncateText="..."
                        />
                      </h2>
                    </div>
                    <div className="flex items-center gap-4 relative">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full">
                          {!event.submittedBy.profilePicture ? (
                            <div className="w-full h-full rounded-full bg-gray-500"></div>
                          ) : (
                            <img
                              src={resolveImgUrl(
                                event.submittedBy.profilePicture
                              )}
                              className="w-full h-full rounded-full object-cover"
                              alt=""
                            />
                          )}
                        </div>
                        <span className="text-xs">
                          {event.submittedBy.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="fa-regular fa-calendar"></i>
                        <span className="text-xs">{`${event.timeUntil}`}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="fa-regular fa-clock"></i>
                        <span className="text-xs">{`${date.getHours()}:${date.getMinutes()}`}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
        <ReactPaginate
          className="react-paginate mt-8"
          breakLabel="..."
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          nextLabel={<i className="fas fa-chevron-right" />}
          previousLabel={<i className="fas fa-chevron-left" />}
          //@ts-ignore
          renderOnZeroPageCount={null}
        />
      </Section>
    </div>
  );
};

const SwiperPrevButton = () => {
  const swiper = useSwiper();
  return (
    <div
      onClick={() => {
        swiper.slidePrev();
      }}
      className={
        "grid place-content-center rounded-full bg-opacity-40 w-10 h-10 " +
        (!swiper.isBeginning
          ? "hover:bg-opacity-75 cursor-pointer bg-white "
          : " bg-gray-300 text-gray-500")
      }
    >
      <i className="fa fas fa-chevron-left"></i>
    </div>
  );
};

const SwiperNextButton = () => {
  const swiper = useSwiper();

  return (
    <div
      onClick={() => {
        swiper.slideNext();
      }}
      className={
        "grid place-content-center rounded-full bg-opacity-40 w-10 h-10 " +
        (!swiper.isEnd
          ? "hover:bg-opacity-75 cursor-pointer bg-white "
          : " bg-gray-300 text-gray-500")
      }
    >
      <i className="fa fas fa-chevron-right"></i>
    </div>
  );
};

export default Events;
